import ExpandableSearch from '@components/v2/ui/ExpandableSearch/ExpandableSearch';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { useMediaQuery, useTheme } from '@mui/material';
import { Plus, ScanBarcode, Search } from 'lucide-react';
import { type ElementType, type ReactNode } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import BarcodeReaderDialog from '../../../../../../modules/orders/products/add-product/barcode-reader/BarcodeReaderDialog';
import SearchStatusAdornment from '../../../../../Search/components/SearchStatusAdornment/SearchStatusAdornment';
import { SearchStatus } from '../../../../../Search/interfaces/search-status.enum';
import { useProductCodeSearchStyles } from './productCodeSearch.styles';
import type { ProductCodeSearchMode, ProductCodeSearchProps } from './productCodeSearch.types';
import useProductCodeSearch from './useProductCodeSearch';

const MODE_ICON: Record<ProductCodeSearchMode, ElementType> = {
  search: Search,
  add: Plus,
};

const MODE_PLACEHOLDER: Record<ProductCodeSearchMode, LangEnum> = {
  search: LangEnum.SEARCH_PRODUCTS_BY_CODE,
  add: LangEnum.ADD_PRODUCT_BY_CODE,
};

const ProductCodeSearch = ({ mode, verifyBrandContext, catalogId }: ProductCodeSearchProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { classes } = useProductCodeSearchStyles();
  const {
    fieldRef,
    value,
    onChange,
    onEnter,
    handleTriggerClick,
    handleOpen,
    handleClose,
    isLoading,
    status,
    errorMessage,
    isOpenBarcodeModal,
    openBarcodeModal,
    closeBarcodeModal,
    onBarcodeSuccess,
  } = useProductCodeSearch({ mode, verifyBrandContext, catalogId });

  const icon = MODE_ICON[mode];

  const errorSlot: ReactNode =
    status === SearchStatus.ERROR || status === SearchStatus.SUCCESS ? (
      <SearchStatusAdornment status={status} errorMessage={errorMessage} />
    ) : undefined;

  return (
    <>
      <ExpandableSearch
        ref={fieldRef}
        trigger={(open) => (
          <IconButton
            icon={icon}
            onClick={() => handleTriggerClick(open)}
            size="medium"
            sx={(theme) => ({
              '&:hover': { backgroundColor: theme.palette.custom.layout.shell.buttonHover },
            })}
          />
        )}
        value={value}
        onChange={onChange}
        onEnter={onEnter}
        onOpen={handleOpen}
        onClose={handleClose}
        placeholder={MODE_PLACEHOLDER[mode]}
        startIcon={icon}
        size="small"
        action={{ icon: ScanBarcode, onClick: openBarcodeModal }}
        errorSlot={errorSlot}
        isLoading={isLoading}
        instantCollapse={isMobile}
        openClassName={isMobile ? classes.mobileOverlay : undefined}
      />

      <BarcodeReaderDialog
        open={isOpenBarcodeModal}
        onClose={closeBarcodeModal}
        onSuccess={onBarcodeSuccess}
      />
    </>
  );
};

export default ProductCodeSearch;
